import { useRef, useCallback, useEffect } from 'react';
import { generalStore } from '@store';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact' | 'invisible';
      }) => string;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
      remove: (widgetId: string) => void;
    };
  }
}

export const useTurnstile = () => {
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const settings = generalStore.settings;
  const isEnabled = !!(settings?.isCaptchaEnabled && settings?.captchaSiteKey);
  const siteKey = settings?.captchaSiteKey;

  const getToken = useCallback(async (): Promise<string | undefined> => {
    if (!isEnabled || !widgetIdRef.current || !window.turnstile) return undefined;
    const token = window.turnstile.getResponse(widgetIdRef.current);
    return token || undefined;
  }, [isEnabled]);

  const resetToken = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  const renderWidget = useCallback((container: HTMLDivElement | null) => {
    if (!container || !isEnabled || !siteKey || !window.turnstile) return;

    // Clean up existing widget if any
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (e) {
        // Widget might already be removed
      }
    }

    containerRef.current = container;
    widgetIdRef.current = window.turnstile.render(container, {
      sitekey: siteKey,
      theme: 'dark',
      size: 'invisible',
    });
  }, [isEnabled, siteKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget might already be removed
        }
      }
    };
  }, []);

  return { getToken, resetToken, isEnabled, siteKey, renderWidget, containerRef };
};



// WEBPACK FOOTER //
// ./src/hooks/useTurnstile/index.ts